import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loading, StatusBadge } from "@/components/common/Primitives";
import { KpiCard } from "@/components/common/KpiCard";
import { fmtEUR, fmtDate } from "@/lib/format";
import { CONTRACT_STATUS_MAP } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import {
  Users, FileText, RotateCw, CheckSquare, AlertTriangle, FolderArchive, Euro,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading) return <Loading />;
  const k = data?.kpis || {};
  const buckets = data?.renewal_buckets || {};
  const chartData = [
    { name: "≤30 T", value: buckets["30"] || 0, fill: "hsl(var(--destructive))" },
    { name: "≤60 T", value: buckets["60"] || 0, fill: "hsl(var(--warning))" },
    { name: "≤90 T", value: buckets["90"] || 0, fill: "hsl(var(--info))" },
    { name: "≤180 T", value: buckets["180"] || 0, fill: "hsl(var(--accent))" },
  ];

  return (
    <div>
      <PageHeader title={`Guten Tag, ${user?.name?.split(" ")[0] || ""}`}
        description="Ihr Überblick über Kunden, Verträge und anstehende Verlängerungen." />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KpiCard testId="kpi-customers" label="Kunden" value={k.customers} icon={Users} onClick={() => navigate("/kunden")} />
        <KpiCard testId="kpi-active-contracts" label="Aktive Verträge" value={k.active_contracts} icon={FileText} onClick={() => navigate("/vertraege")} />
        <KpiCard testId="kpi-upcoming-renewals" label="Anstehende Verlängerungen" value={k.upcoming_renewals} icon={RotateCw} tone="info" onClick={() => navigate("/verlaengerungen")} />
        <KpiCard testId="kpi-monthly-revenue" label="Mtl. Umsatz (aktiv)" value={fmtEUR(k.monthly_revenue)} icon={Euro} />
        <KpiCard testId="kpi-open-tasks" label="Offene Aufgaben" value={k.open_tasks} icon={CheckSquare} onClick={() => navigate("/aufgaben")} />
        <KpiCard testId="kpi-overdue-tasks" label="Überfällige Aufgaben" value={k.overdue_tasks} icon={AlertTriangle} tone="danger" onClick={() => navigate("/aufgaben")} />
        <KpiCard testId="kpi-unmatched-docs" label="Ungeprüfte Dokumente" value={k.unmatched_documents} icon={FolderArchive} tone="warning" onClick={() => navigate("/dokumente")} />
        <KpiCard testId="kpi-renewed" label="Verlängert / Verloren" value={`${k.renewed} / ${k.lost}`} icon={RotateCw} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Verlängerungen nach Frist" className="lg:col-span-1">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Nächste Vertragsabläufe" className="lg:col-span-2">
          <div className="space-y-2">
            {(data?.upcoming || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Keine anstehenden Abläufe.</p>}
            {(data?.upcoming || []).map((c) => (
              <div key={c.id} onClick={() => navigate(`/vertraege/${c.id}`)}
                className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2.5 hover:bg-muted/40"
                data-testid={`dashboard-upcoming-${c.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{c.customer_name}</div>
                  <div className="truncate text-xs text-muted-foreground">{c.provider} · {c.tariff_name}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={cn_days(c.days_to_end)}>{c.days_to_end} T</span>
                  <StatusBadge status={c.status} map={CONTRACT_STATUS_MAP} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Überfällige Aufgaben" className="mt-4">
        <div className="space-y-2">
          {(data?.overdue_tasks || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Keine überfälligen Aufgaben. 🎯</p>}
          {(data?.overdue_tasks || []).map((t) => (
            <div key={t.id} onClick={() => navigate("/aufgaben")}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2.5">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{t.title}</div>
                <div className="text-xs text-muted-foreground">Fällig: {fmtDate(t.due_date)}</div>
              </div>
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function cn_days(d) {
  const base = "rounded-md px-2 py-0.5 text-xs font-semibold ";
  if (d == null) return base + "bg-muted text-muted-foreground";
  if (d <= 30) return base + "bg-destructive/10 text-destructive";
  if (d <= 60) return base + "bg-amber-500/10 text-amber-600 dark:text-amber-400";
  return base + "bg-muted text-muted-foreground";
}
